import { Link } from "react-router-dom";
import Profile from "../assets/profile.jpg"; // Default profile image

// Function to create SEO-friendly slugs
const createSlug = (text) => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-");
};

// Price parsing utility function
const parsePrice = (price) => {
  if (price === null || price === undefined || price === "") return null;

  // Handle string prices with commas, currency symbols, etc.
  if (typeof price === "string") {
    // Remove commas, currency symbols, and any non-numeric characters except decimal point
    const cleaned = price.replace(/[$,]/g, "").trim();
    // If the cleaned string is empty, return null
    if (cleaned === "") return null;
    return Number(cleaned);
  }

  return Number(price);
};

const PropertyList = ({
  id,
  images = [],
  videos = [],
  pstatus = "Available",
  address = {},
  priceguide = 0,
  ptitle = "No Title",
  preason,
  beds = 0,
  baths = 0,
  land_size = 0,
  agent = {},
}) => {
  // Proper price handling
  const priceNumber = parsePrice(priceguide);
  const isValidPrice = !isNaN(priceNumber) && priceNumber >= 0;
  const shouldShowPrice = isValidPrice && priceNumber > 0;

  const safeAgent = agent || {};

  const agentImage = safeAgent.profileimg || Profile;
  const agentName = safeAgent.firstname
    ? `${safeAgent.firstname} ${safeAgent.lastname}`
    : "UrbanView Real Estate";
  const agentRole = safeAgent.designation || "Property Manager";

  const imageCount = images.length;
  const videoCount = videos.length;

  const imageUrl =
    images.length > 0 && images[0]?.path
      ? images[0].path
      : "/default-property.jpg";

  const propertySlug = createSlug(ptitle);
  const agentSlug = createSlug(agentName);

  // Filter out zero values for beds, baths, and land_size
  const propertyFeatures = [
    { label: "Beds", value: beds, icon: "bi-bed", show: beds > 0 },
    { label: "Baths", value: baths, icon: "bi-bath", show: baths > 0 },
    {
      label: "sqm",
      value: land_size,
      icon: "bi-rulers",
      show: land_size > 0,
    },
  ].filter((feature) => feature.show);

  return (
    <div className="card rounded-0 border-0 shadow-sm w-100">
      <div
        className="position-relative image-container w-100"
        style={{ height: "280px", overflow: "hidden" }}
      >
        <Link
          to={`/property-details/${propertySlug}`}
          className="text-decoration-none text-dark"
        >
          <img
            src={imageUrl}
            alt={ptitle || "Property"}
            className="card-img-top img-fluid"
            style={{ objectFit: "cover", height: "100%", width: "100%" }}
          />
        </Link>
        <span
          className={`position-absolute top-0 end-0 m-3 text-white fw-bold px-3 rounded-2 py-1 shadow-sm ${
            pstatus === "Available"
              ? "bg-success"
              : pstatus === "Leased"
              ? "bg-warning"
              : "bg-danger"
          }`}
        >
          {pstatus === "Sell" ? "Sold" : pstatus}
        </span>
        <div
          className="position-absolute bottom-0 start-0 d-flex justify-content-between text-white p-2 w-100"
          style={{ background: "rgba(0, 0, 0, 0.5)" }}
        >
          <p className="m-0">
            <i className="bi bi-geo-alt-fill me-1"></i>{" "}
            {address.street_address || "Address not available"}
          </p>

          {/* Only show media badges if count is greater than 0 */}
          {(imageCount > 0 || videoCount > 0) && (
            <div className="d-flex gap-3">
              {imageCount > 0 && (
                <span className="bg-dark d-flex align-items-center px-2 py-1 gap-1 rounded">
                  <i className="bi bi-camera"></i> {imageCount}
                </span>
              )}
              {videoCount > 0 && (
                <span className="bg-dark d-flex align-items-center px-2 py-1 gap-1 rounded">
                  <i className="bi bi-film"></i> {videoCount}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card-body text-start p-3">
        {/* Final price display logic - Show "/Weekly" only for Leased status */}
        {shouldShowPrice ? (
          <h6 className="text-primary fw-bold">
            ${priceNumber.toLocaleString()}
            {pstatus === "Leased" && <span className="small">/Weekly</span>}
          </h6>
        ) : (
          <h6 className="text-primary fw-bold">Contact the seller</h6>
        )}

        <Link
          to={`/property-details/${propertySlug}`}
          className="text-decoration-none text-dark"
        >
          <h4 className="fw-bold">{ptitle}</h4>
        </Link>
        <p className="text-muted">{address.suburb || "No suburb info"}</p>

        {/* Property Features - Only show non-zero values */}
        {propertyFeatures.length > 0 && (
          <div className="d-flex gap-1 text-muted">
            {propertyFeatures.map((feature, index) => (
              <div
                key={feature.label}
                className={`px-2 ${
                  index < propertyFeatures.length - 1 ? "border-end" : ""
                }`}
              >
                <i className={`bi ${feature.icon} me-1`}></i> {feature.value}{" "}
                {feature.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Agent Section */}
      <div className="card-footer m-3 bg-white d-flex align-items-center mt-3 border-top pt-3">
        <Link
          to={`/team-details/${agentSlug}`}
          aria-label={`View profile of ${agentName}`}
        >
          <img
            src={agentImage}
            alt={agentName}
            className="rounded-circle me-2"
            style={{ width: "40px", height: "40px" }}
          />
        </Link>
        <div className="text-start">
          <Link
            to={`/team-details/${agentSlug}`}
            className="text-decoration-none text-dark"
            aria-label={`View profile of ${agentName}`}
          >
            <h6 className="mb-0">{agentName}</h6>
          </Link>
          <p className="text-muted small mb-0">{agentRole}</p>
        </div>
        <div className="ms-auto d-flex gap-2">
          <Link
            to={`/team-details/${agentSlug}`}
            aria-label={`View profile of ${agentName}`}
          >
            <i className="bi bi-person-add text-muted bg-light py-1 px-2"></i>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PropertyList;
